import { useEffect, useRef } from 'react';
import { FormControlConstants, type FormFieldModel } from '@generated';
import { type FormikValues } from 'formik';
import { useForm } from '~/shared/forms/FormContext';
import type { FormValues } from '~/shared/forms/types';
import { satisifiesWhenConditions } from '../when';
import { getFormFieldDefaultValue } from '../values';

export function FieldActions({ formFields }: { formFields: FormFieldModel[] }) {
	const { formHelpers } = useForm();
	const prevFormValuesRef = useRef<FormValues | null>(formHelpers.initialValues);

	const formValues = formHelpers.values;
	useEffect(() => {
		const previousValues = prevFormValuesRef.current;
		prevFormValuesRef.current = formValues;

		const changedFields = getChangedFields(previousValues, formValues);
		const actions = computeActions(formFields, formValues, formHelpers.initialValues, changedFields);
		if (actions.length === 0) {
			return;
		}

		const runActions = async () => {
			const touchedUpdates: Record<string, boolean> = {};
			for (const action of actions) {
				touchedUpdates[action.name] = true;
			}
			await formHelpers.setValues(
				(prev: FormikValues) => {
					const updated = { ...prev };
					for (const action of actions) {
						updated[action.name] = action.value;
					}
					return updated;
				},
				true,
				touchedUpdates
			);
		};

		runActions();
	}, [formValues]); // eslint-disable-line react-hooks/exhaustive-deps

	return null;
}

function getChangedFields(prev: FormValues | null, current: FormValues): Set<string> | null {
	if (prev === null) {
		return null;
	}
	const changed = new Set<string>();
	const allKeys = new Set([...Object.keys(prev), ...Object.keys(current)]);
	for (const key of allKeys) {
		const prevVal = prev[key];
		const currVal = current[key];
		if (prevVal !== currVal && JSON.stringify(prevVal) !== JSON.stringify(currVal)) {
			changed.add(key);
		}
	}
	return changed;
}

interface SetFieldAction {
	name: string;
	value: unknown;
}

export function computeActions(
	formFields: FormFieldModel[],
	formValues: FormValues,
	initialFormValues: FormValues,
	changedFields: Set<string> | null
): SetFieldAction[] {
	if (!changedFields || changedFields.size === 0) {
		return [];
	}

	const fieldActionsToRun: SetFieldAction[] = [];

	for (const formField of formFields) {
		if (!formField.fieldActions || formField.fieldActions.length === 0) {
			continue;
		}
		for (const action of formField.fieldActions) {
			if (action.when.length > 0 && !action.when.some(rule => rule.name.split('|').some(name => changedFields.has(name)))) {
				continue;
			}

			if (action.when.length > 0 && !satisifiesWhenConditions(action.when, formValues, action.whenCondition, changedFields)) {
				continue;
			}

			if (action.action === FormControlConstants.formFieldActionTypes.set && formValues[formField.name] !== action.value) {
				fieldActionsToRun.push({ name: formField.name, value: action.value });
				break;
			} else if (
				action.action === FormControlConstants.formFieldActionTypes.reset &&
				!equalsValue(getFormFieldDefaultValue(formField), formValues[formField.name], formField)
			) {
				fieldActionsToRun.push({ name: formField.name, value: getFormFieldDefaultValue(formField) });
				break;
			} else if (action.action === FormControlConstants.formFieldActionTypes.clear && formValues[formField.name]) {
				fieldActionsToRun.push({
					name: formField.name,
					value: formField.dataType === FormControlConstants.formControlDataTypes.stringArray ? [] : ''
				});
				break;
			} else if (
				action.action === FormControlConstants.formFieldActionTypes.resetIfEquals &&
				equalsValue(action.value, formValues[formField.name], formField)
			) {
				fieldActionsToRun.push({ name: formField.name, value: initialFormValues[formField.name] });
				break;
			} else if (action.action === FormControlConstants.formFieldActionTypes.copy) {
				fieldActionsToRun.push({ name: formField.name, value: formValues[action.value!] });
				break;
			}
		}
	}
	return fieldActionsToRun;
}

export function equalsValue<T>(intialValue: T, currentValue: T, formFieldModel: FormFieldModel) {
	if (
		formFieldModel.dataType === FormControlConstants.formControlDataTypes.stringArray ||
		formFieldModel.dataType === FormControlConstants.formControlDataTypes.objectArray
	) {
		return JSON.stringify(intialValue) === JSON.stringify(currentValue);
	}
	return intialValue === currentValue;
}
